import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useState } from 'react';
import { Settings, Moon, Sun, Bell, Database, Fuel } from 'lucide-react';
import { toast } from 'sonner';
import { seedDemoData } from '../lib/api';

export default function SettingsPage() {
    const [darkMode, setDarkMode] = useState(document.documentElement.classList.contains('dark'));
    const [notifications, setNotifications] = useState(true);
    const [seeding, setSeeding] = useState(false);

    const toggleDarkMode = () => {
        document.documentElement.classList.toggle('dark');
        setDarkMode(!darkMode);
        toast.success(`Mode ${darkMode ? 'Terang' : 'Gelap'} diaktifkan`);
    };

    const handleSeedData = async () => {
        try {
            setSeeding(true);
            await seedDemoData();
            toast.success('Demo data berhasil dimuat');
        } catch (error) {
            console.error('Failed to seed data:', error);
            if (error.response?.data?.message === 'Data sudah ada') {
                toast.info('Data demo sudah ada');
            } else {
                toast.error('Gagal memuat demo data');
            }
        } finally {
            setSeeding(false);
        }
    };

    return (
        <Layout title="Pengaturan">
            <div className="max-w-2xl space-y-6" data-testid="settings-page">
                {/* Appearance */}
                <Card className="industrial-card">
                    <CardHeader className="border-b border-border pb-4">
                        <CardTitle className="section-title text-lg flex items-center gap-2">
                            <Settings className="w-5 h-5 text-primary" />
                            Tampilan
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                {darkMode ? (
                                    <Moon className="w-5 h-5 text-muted-foreground" />
                                ) : (
                                    <Sun className="w-5 h-5 text-muted-foreground" />
                                )}
                                <div>
                                    <Label htmlFor="dark-mode" className="font-medium">Mode Gelap</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Ubah tampilan antara mode terang dan gelap
                                    </p>
                                </div>
                            </div>
                            <Switch
                                id="dark-mode"
                                checked={darkMode}
                                onCheckedChange={toggleDarkMode}
                                data-testid="dark-mode-switch"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Notifications */}
                <Card className="industrial-card">
                    <CardHeader className="border-b border-border pb-4">
                        <CardTitle className="section-title text-lg flex items-center gap-2">
                            <Bell className="w-5 h-5 text-primary" />
                            Notifikasi
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label htmlFor="notifications" className="font-medium">Aktifkan Notifikasi</Label>
                                <p className="text-sm text-muted-foreground">
                                    Terima notifikasi untuk maintenance due, work order, dan dokumen expired
                                </p>
                            </div>
                            <Switch
                                id="notifications"
                                checked={notifications}
                                onCheckedChange={setNotifications}
                                data-testid="notifications-switch"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Data Management */}
                <Card className="industrial-card">
                    <CardHeader className="border-b border-border pb-4">
                        <CardTitle className="section-title text-lg flex items-center gap-2">
                            <Database className="w-5 h-5 text-primary" />
                            Manajemen Data
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="space-y-4">
                            <div>
                                <Label className="font-medium">Muat Demo Data</Label>
                                <p className="text-sm text-muted-foreground mb-3">
                                    Isi sistem dengan data contoh armada, sopir, jadwal service, dan work order untuk testing
                                </p>
                                <Button 
                                    onClick={handleSeedData}
                                    disabled={seeding}
                                    className="industrial-btn-primary"
                                    data-testid="seed-data-settings-btn"
                                >
                                    {seeding ? 'Memuat...' : 'Muat Demo Data'}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* About */}
                <Card className="industrial-card">
                    <CardHeader className="border-b border-border pb-4">
                        <CardTitle className="section-title text-lg flex items-center gap-2">
                            <Fuel className="w-5 h-5 text-primary" />
                            Tentang
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="space-y-2 text-sm">
                            <p><span className="text-muted-foreground">Aplikasi:</span> Zenith Asset Intelligence</p>
                            <p><span className="text-muted-foreground">Versi:</span> 2.0.0</p>
                            <p><span className="text-muted-foreground">Deskripsi:</span> Sistem Manajemen Preventif Armada Pengiriman Solar Industry</p>
                            <div className="pt-4 border-t mt-4">
                                <p className="text-muted-foreground text-xs">
                                    Fitur: Manajemen Armada, Sopir, Trip Log, Jadwal Service berbasis KM/Waktu, 
                                    Work Orders, Tracking Dokumen (KIR/STNK/SIM), Laporan Biaya & Efisiensi BBM
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
